import { useState } from "react";
import { Input } from "@shop-ledger/ui-components";

const ADD_NEW = "__add_new__";

export interface CategorySelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (category: string) => void;
  /** Distinct categories already in use (derived client-side from the loaded inventory
   * list — there's no dedicated `/categories` endpoint, categories are free-text on each
   * item). */
  existingCategories: string[];
}

/** Category field for the product form: a dropdown of categories already in use on other
 * items, plus an "Add new category" option that swaps in a free-text `Input` (business-
 * owner request — previously this was always a bare text field with no visibility into
 * what categories already existed, inviting near-duplicate spellings). */
export function CategorySelect({ id, label, value, onChange, existingCategories }: CategorySelectProps) {
  const [addingNew, setAddingNew] = useState(false);

  if (addingNew) {
    return (
      <div className="sl-field">
        <Input id={id} label={label} value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. Beverages" />
        {existingCategories.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setAddingNew(false);
              onChange("");
            }}
            style={{
              alignSelf: "flex-start",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontSize: "0.8125rem",
              color: "var(--sl-color-primary)",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Choose from existing categories instead
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="sl-field">
      <label className="sl-field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="sl-input"
        value={existingCategories.includes(value) || value === "" ? value : ADD_NEW}
        onChange={(e) => {
          if (e.target.value === ADD_NEW) {
            setAddingNew(true);
            onChange("");
          } else {
            onChange(e.target.value);
          }
        }}
      >
        <option value="">No category</option>
        {existingCategories.map((cat) => (
          <option key={cat} value={cat}>
            {cat}
          </option>
        ))}
        <option value={ADD_NEW}>+ Add new category…</option>
      </select>
    </div>
  );
}
